/**
 * batch-67 meldingen-ontvangers (FO-500) - de INDELING van de doelgroep-resolvers.
 *
 * Twee vragen hangen af van wat voor doelgroep een resolver is, en ze werden tot
 * nu toe op twee plekken los van elkaar beantwoord (een allowlist in het
 * interceptiepunt, een `via`-lijst in de route). Dat liep uit de pas: een
 * monteur kon met een tik op "Ik hoor dit niet te krijgen" al zijn
 * werkbon-toewijzingen uitzetten, en een lege groep viel voor de ene resolver
 * terug op de eigenaar en voor de andere stil op niemand.
 *
 *  1. LEEG -> EIGENAAR? Levert de resolver niemand op, is dat dan een
 *     configuratiefout die bij de eigenaar hoort te landen (met een signaal aan
 *     beheer), of betekent het gewoon "niemand"?
 *  2. UITSLUITBAAR? Mag een ontvanger zichzelf met "Ik hoor dit niet te krijgen"
 *     uitsluiten (EXCLUDE), of gaat de melding over hemzelf of zijn eigen taak en
 *     seint de knop dan alleen beheer?
 *
 * De vier soorten:
 *
 *   GROEP           Een rol-, permissie- of instelbare groep. Leeg is een
 *                   configuratiefout: naar de eigenaar. Wie er als groepslid in zit
 *                   mag zichzelf uitsluiten.
 *   GERICHT         De aangewezen verantwoordelijke van de bron (de teamleider van
 *                   de monteur, de inkoop-behandelaar), of de betrokkene plus een
 *                   groep. Leeg betekent dat er voor deze bron niemand is
 *                   aangewezen: naar de eigenaar. Niet zelf uit te sluiten: de
 *                   melding is aan de persoon gericht, en een uitsluiting zou
 *                   hem voor al zijn eigen bronnen dempen.
 *   PERSOON         De betrokkene zelf, een door de aanroeper aangeleverde
 *                   persoon, of een bewust dichte (fail-closed) of vertrouwelijke
 *                   doelgroep. Leeg betekent niemand; de tekst mag niet bij een
 *                   ander landen. Niet zelf uit te sluiten.
 *   GESLOTEN_GROEP  Een groep met een eigen afscherming (vertrouwenspersonen, de
 *                   mijlpalen van een contract zonder de medewerker zelf). Leeg
 *                   betekent niemand: de eigenaar-terugval kent die afscherming
 *                   niet. Juist daarom niet zelf uit te sluiten: klikt het laatste
 *                   lid zich weg, dan bereikt de melding niemand meer en is er
 *                   geen terugval. De knop seint dan alleen beheer, en de levering
 *                   seint beheer als uitsluitingen de groep toch leegmaken.
 *
 * NIEUWE RESOLVER? Deel hem hier in. De drift-test
 * `apps/api/src/lib/notificatie-events/__tests__/resolver-indeling-drift.test.ts`
 * faalt op een geregistreerde resolver die hier ontbreekt en noemt de sleutel.
 * Twijfel je: `PERSOON` houdt het gedrag van voor FO-500 (leeg = niemand, niet uit
 * te sluiten).
 *
 * De indeling staat in @streep/types en niet in de api, omdat ook de
 * "Waarom"-zin onder een melding hem gebruikt: "je hoort bij de doelgroep ..."
 * klopt voor een groep, maar niet voor de monteur van de werkbon. Bladmodule
 * zonder imports.
 */

export type ResolverSoort = 'GROEP' | 'GERICHT' | 'PERSOON' | 'GESLOTEN_GROEP'

export const RESOLVER_SOORT: Readonly<Record<string, ResolverSoort>> = {
  // ── GROEP: rol-, permissie- en instelbare groepen ─────────────────────────
  'leave.approvers': 'GROEP',
  'leave.teamleiders': 'GROEP',
  'all-hr': 'GROEP',
  'all-hr+salaris': 'GROEP',
  'intake.team': 'GROEP',
  'hr+manager': 'GROEP',
  // Zonder INCLUDE-rijen leeg (was: iedereen met portal-toegang) - FO-500 §1.2.
  'configurable.recipients': 'GROEP',
  planner: 'GROEP',
  'planner+teamleider': 'GROEP',
  'planner+hr': 'GROEP',
  manager: 'GROEP',
  'planning.planners': 'GROEP',
  administratie: 'GROEP',
  // batch-69 werkbon-pauze (integrator-review H1): een permissiegroep.
  'klantpauze.oplossers': 'GROEP',
  'veiligheid.opvolgers': 'GROEP',
  'jubileum.hr+teamleider': 'GROEP',
  'vervanging.opvolgers': 'GROEP',
  'voorraad.beheer': 'GROEP',
  'uitrusting.verwerkers': 'GROEP',
  'uitrusting.picklijst': 'GROEP',
  'loonstrook.verantwoordelijken': 'GROEP',
  'loonstrook.beheerders': 'GROEP',
  'loonstrook.verzoek-beoordelaars': 'GROEP',
  'loonstrook.vrijgave-bewakers': 'GROEP',
  'idee.ontvangers': 'GROEP',
  'idee.organisatie': 'GROEP',
  'voertuigen.beheer': 'GROEP',
  // batch-69 km-balans: wagenparkbeheer met kostprijsinzage.
  'voertuigen.beheer+kostprijs': 'GROEP',
  // Wie de skillmatrix mag beoordelen.
  'skills.beoordelaars': 'GROEP',
  // batch-71 gesprek-secties (FO-537): de houders uit de module-instelling.
  'gesprekcontrole.houders': 'GROEP',
  // batch-71 gesprek-secties (FO-536): de aangewezen eigenaar van het doel (leeg -> de eigenaar van de melding).
  'ontwikkeldoel.eigenaar': 'GERICHT',
  // batch-70 voertuig-meldingen: wagenparkbeheer of de loonstrook-verantwoordelijken per kanaal.
  'voertuig-melding.doelgroep': 'GROEP',
  'verzuim.contactpersonen': 'GROEP',
  'all-admins': 'GROEP',
  // batch-71 kiosk-magazijn: HR en ADMIN.
  'kiosk.beheerders': 'GROEP',
  'facturen.approvers': 'GROEP',
  'reisafwijking.beoordelaars': 'GROEP',
  'klantverzoeken.ontvangers': 'GROEP',
  // Zonder ingestelde escalatie-ontvangers leeg (was: alle managers) - FO-500 §B3.
  'klantverzoeken.escalatie': 'GROEP',
  'abonnementen.beheer': 'GROEP',
  'inkoop.goedkeurders': 'GROEP',
  'voorraad.minimumbeheer': 'GROEP',
  directie: 'GROEP',
  facilities: 'GROEP',

  // ── GERICHT: de aangewezen verantwoordelijke, of de betrokkene plus een groep ──
  'employee.self+hr': 'GERICHT',
  'employee.self+hr+tl': 'GERICHT',
  'employee.self+tl': 'GERICHT',
  'employee.self+salaris': 'GERICHT',
  'onboarding.hr+medewerker': 'GERICHT',
  'consignatie.ruil-collega+planner': 'GERICHT',
  'consignatie.ruil-aanvrager+planner': 'GERICHT',
  'cert.hr+verantwoordelijke': 'GERICHT',
  // batch-71 verplichte-certificaten (FO-541): HR plus de teamleider van de medewerker.
  'verplichte-certificaten.hr+tl': 'GERICHT',
  // Permissiegroep plus de invuller / de persoonlijk betrokkenen.
  'ongevallen.beheerders': 'GERICHT',
  'relatie-onboarding.beoordelaars': 'GERICHT',
  // Zonder aangewezen persoon leeg (was: een hele rol of permissiegroep) - FO-500 §1.2.
  'inkoop.behandelaar': 'GERICHT',
  'inkoopbudget.budgethouder': 'GERICHT',
  'werkbon.teamleider': 'GERICHT',
  'reisuren.teamleider': 'GERICHT',
  'kandidaat.recruiter-of-hr': 'GERICHT',
  // FO-517 — instelbare groepen (standaard per rol) en het gerichte info-verzoek.
  'kandidaat.beoordelaars': 'GROEP',
  'kandidaat.besluitnemers': 'GROEP',
  'kandidaat.info-verzoek': 'GERICHT',

  // ── PERSOON: de betrokkene zelf, een aangeleverde persoon, of bewust dicht ──
  'werkbon.assigned-employee': 'PERSOON',
  'werkbon.previous-employee': 'PERSOON',
  'leave.requester': 'PERSOON',
  'actor.requester': 'PERSOON',
  'employee.self': 'PERSOON',
  'verjaardag.medewerker': 'PERSOON',
  'uitrusting.besteller': 'PERSOON',
  'schade.bestuurder': 'PERSOON',
  'idee.indiener': 'PERSOON',
  'taak.uitvoerder': 'PERSOON',
  'taak.aanmaker': 'PERSOON',
  'cert.betrokkenen': 'PERSOON',
  'consignatie.ruil-aanvrager+collega': 'PERSOON',
  'acquisitie.checkin-verantwoordelijke': 'PERSOON',
  'acquisitie.vervolg-eigenaar': 'PERSOON',
  // Vertrouwelijk: liever stilte dan een melding bij een ander.
  'gesprek.recipient': 'PERSOON',
  'hr-gesprek.betrokkenen': 'PERSOON',
  'kluis.eigenaar': 'PERSOON',
  'kluis.vrijgave.beoordelaars': 'PERSOON',
  'kluis.vrijgave.betrokkenen': 'PERSOON',
  // Bewust dicht (fail-closed) of met een eigen terugval in de module zelf.
  'verjaardag.teamleider': 'PERSOON',
  'client.responsible': 'PERSOON',
  'lead.owner': 'PERSOON',
  'taak.teamleider': 'PERSOON',
  'taak.uitvoerder-tl': 'PERSOON',
  'taak.escalatie': 'PERSOON',
  'taak.escalatie-aanmaker': 'PERSOON',
  'idee.reactie-teamleider': 'PERSOON',
  'mail.afzender-of-admin': 'PERSOON',
  'uitrusting.levering.beoordelaar': 'PERSOON',

  // ── GESLOTEN_GROEP: een groep met een eigen afscherming ───────────────────
  'all-vertrouwenspersonen': 'GESLOTEN_GROEP',
  'contract-mijlpaal.doelgroep': 'GESLOTEN_GROEP',
  'contract-mijlpaal.escalatie': 'GESLOTEN_GROEP',
  'contract-mijlpaal.hr': 'GESLOTEN_GROEP',
  // Noemt een medewerker bij naam; alleen HR en zijn teamleider.
  'boetes.rijgedrag': 'GESLOTEN_GROEP',

  // ── De resolvers van de andere batch-67-streams ───────────────────────────
  // Ze bestaan op deze branch nog niet; na de merge zijn ze meteen ingedeeld
  // (zie `RESOLVERS_VAN_ANDERE_STREAMS` in de drift-test). Heet een resolver na
  // de merge anders, dan faalt de drift-test en noemt hij de nieuwe sleutel.
  'campagne.beheerders': 'GROEP',
  'campagne.deelnemer': 'PERSOON',
  'evenement.organisatie': 'GROEP',
  'reparatie.goedkeurders': 'GROEP',
  'middel.overdracht-ontvanger': 'PERSOON',
  'taak.deadline-escalatie': 'PERSOON',
  // Een HR-besluit gaat over een medewerker die het zelf nooit mag krijgen.
  'hr-besluit.behandelaars': 'GESLOTEN_GROEP',
  'hr-besluit.behandelaars+directie': 'GESLOTEN_GROEP',
  'hr-besluit.behandelaars+salaris': 'GESLOTEN_GROEP',
  'hr-besluit.controleurs': 'GESLOTEN_GROEP',
  'hr-besluit.stap-verantwoordelijke': 'GESLOTEN_GROEP',
  // FO-508 (main, tijdens batch-67): rol HR of ADMIN met backoffice-toegang, de
  // gate van de goedkeur-route. Een rolgroep: leeg -> eigenaar, nooit stil naar niemand.
  'middel-uitzondering.beoordelaars': 'GROEP',
  // batch-68 controle (FO-342 §7): PAYROLL.canApprove (zonder teamleider/accountant).
  'payroll.approvers': 'GROEP',
  // batch-69 bestelflow (FO-512): de beoordelaars zijn een permissiegroep (leeg ->
  // eigenaar); de goedkeurder van DEZE bestelling is een persoon (weg = niemand).
  'uitrusting.beoordelaars': 'GROEP',
  'uitrusting.goedkeurder': 'PERSOON',
  // batch-69 planbord-afspraak (FO-523): de deelnemers van de afspraak (tweede
  // persoon, nooit bij een ander) en, na 48 uur zonder verslag, hun teamleider
  // plus de accountmanager van de klant (gericht; leeg -> eigenaar).
  'afspraak.deelnemers': 'PERSOON',
  'afspraak.escalatie': 'GERICHT',
  // batch-69 actiepunten (FO-514): de toegewezen persoon of de dragers van de
  // verantwoordelijke rol van DIT punt. Leeg = niemand aangewezen: naar de eigenaar.
  'actiepunt.verantwoordelijke': 'GERICHT',
  // batch-69 actiepunten (FO-514): de directie, plus bij nieuwe punten de dragers
  // van de rollen die ze kregen. Een groep, geen persoon van een bron.
  'actiepunten.samenvatting': 'GROEP',
  // batch-69 magazijn-cijfers (FO-520): VOORRAAD.canApprove (zonder accountant).
  'voorraad.goedkeurders': 'GROEP',
  // batch-70 app-dashboard (FO-526): de beheerders plus de ingestelde ontvangers van
  // APP_DEPLOY_LIVE. Een groep (leeg = geen beheerder actief: naar de eigenaar).
  'app-dashboard.doelgroep': 'GROEP',
  // batch-71 workflow-sjablonen (FO-533): de lijsten op WorkflowInstelling.
  'workflow.doelgroep': 'GROEP',
  // batch-71 kennismaking (FO-534): de aangewezen verantwoordelijke (plus HR) en de
  // houder van een actiepunt zijn gericht: leeg = niemand aangewezen, dus naar de
  // eigenaar. De escalatie volgt de doelgroep van de mijlpaal (afgeschermd, zoals M3 en M8).
  'kennismaking.verantwoordelijke': 'GERICHT',
  'kennismaking.verantwoordelijke+hr': 'GERICHT',
  'kennismaking.escalatie': 'GESLOTEN_GROEP',
  'kennismaking.actiepunt-houder': 'GERICHT',
}

/** De soort van een resolver; `null` voor een onbekende (niet ingedeelde) sleutel. */
export function resolverSoort(key: string | null | undefined): ResolverSoort | null {
  if (!key) return null
  return Object.prototype.hasOwnProperty.call(RESOLVER_SOORT, key)
    ? (RESOLVER_SOORT[key] ?? null)
    : null
}

/**
 * Hoort een LEGE uitkomst van deze doelgroep naar de eigenaar te gaan?
 * Een onbekende resolver: nee (het gedrag van voor FO-500).
 */
export function soortValtTerugOpEigenaar(soort: ResolverSoort | null | undefined): boolean {
  return soort === 'GROEP' || soort === 'GERICHT'
}

/**
 * Mag een ontvanger uit deze doelgroep zichzelf uitsluiten (EXCLUDE)? Alleen
 * een lid van een gewone groep: daar vangt de eigenaar een leeggeklikte
 * doelgroep op. Een afgeschermde groep en een onbekende resolver: nee, de knop
 * seint dan alleen beheer.
 */
export function soortIsUitsluitbaar(soort: ResolverSoort | null | undefined): boolean {
  return soort === 'GROEP'
}
